import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Button, ConfirmDialog } from '../../components/ui';
import { ServiceListView, ServiceModal, ServiceViewModal } from '../../components/services';
import { ApiError } from '../../api';
import usePermissions from '../../hooks/usePermissions';
import useStaffServicesStore from '../../stores/staffServicesStore';
import type { Service } from '../../stores/servicesStore';
import { PERMISSION } from '../../types/permissions';

/**
 * Services for the Staff portal. Reuses the Provider Settings → Services UI,
 * minus the CSV/bulk-upload flow, which stays a provider-only capability.
 *
 * SERVICE_VIEW gets the list and the read-only detail modal; SERVICE_MANAGE
 * additionally gets add / edit / delete. The route itself is gated in the
 * router, so reaching this component already implies at least SERVICE_VIEW —
 * `canManage` is what varies here.
 */
const StaffServices = () => {
	const { services, isLoading, error, fetchServices, addService, updateService, deleteService } =
		useStaffServicesStore();
	const { has } = usePermissions();
	const canManage = has(PERMISSION.SERVICE_MANAGE);

	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingService, setEditingService] = useState<Service | null>(null);
	const [viewingService, setViewingService] = useState<Service | null>(null);
	const [deletingService, setDeletingService] = useState<Service | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		fetchServices();
	}, [fetchServices]);

	const openAddModal = () => {
		setEditingService(null);
		setIsModalOpen(true);
	};

	const openEditModal = (service: Service) => {
		setEditingService(service);
		setIsModalOpen(true);
	};

	const handleSave = async (service: Service) => {
		try {
			if (editingService) {
				await updateService(editingService.id, service);
				toast.success('Service updated successfully!');
			} else {
				await addService(service);
				toast.success('Service added successfully!');
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to save service');
			// Rethrow so the modal stays open with the user's edits intact.
			throw err;
		}
	};

	const confirmDelete = async () => {
		if (!deletingService) return;

		setIsDeleting(true);
		try {
			await deleteService(deletingService.id);
			toast.success('Service deleted successfully!');
			setDeletingService(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete service');
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<AppLayout>
			<div className="settings-card">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<WrenchScrewdriverIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Your Services</h2>
						<p className="settings-card__subtitle">
							{canManage
								? 'Manage your salon services and pricing'
								: 'Services offered at your location'}
						</p>
					</div>
					{canManage && (
						<Button
							variant="primary"
							size="sm"
							onClick={openAddModal}
							className="hidden sm:flex"
						>
							<PlusIcon className="w-4 h-4 mr-1" />
							Add New Service
						</Button>
					)}
				</div>

				<div className="settings-card__body">
					<ServiceListView
						services={services}
						isLoading={isLoading}
						error={error}
						searchTerm={searchTerm}
						onSearchChange={setSearchTerm}
						canManage={canManage}
						onAdd={openAddModal}
						onView={setViewingService}
						onEdit={openEditModal}
						onDelete={setDeletingService}
						onRetry={fetchServices}
					/>
				</div>
			</div>

			{canManage && (
				<ServiceModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					service={editingService}
					canMarkAddOn
					onSave={handleSave}
					onDelete={(id: string) => {
						setIsModalOpen(false);
						const svc = services.find(s => s.id === id);
						if (svc) setDeletingService(svc);
					}}
				/>
			)}

			<ServiceViewModal
				service={viewingService}
				onClose={() => setViewingService(null)}
				onEdit={
					canManage
						? service => {
								setViewingService(null);
								openEditModal(service);
							}
						: undefined
				}
			/>

			<ConfirmDialog
				isOpen={!!deletingService}
				onClose={() => setDeletingService(null)}
				onConfirm={confirmDelete}
				title="Delete Service"
				message={`Are you sure you want to delete "${deletingService?.name}" and all its pricing combinations? This action cannot be undone.`}
				confirmLabel="Yes, Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</AppLayout>
	);
};

export default StaffServices;
